import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PROBLEM, BOARD, DEPS, LOOP, CODE, GATE, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const strike = interpolate(frame, [40, 75], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="multi-agent missions · coordination" color={COLORS.cyan} />
          <Big>
            5 agents finished one mission
            <br />
            and{" "}
            <span style={{ position: "relative", color: COLORS.red }}>
              never spoke
              <span
                style={{
                  position: "absolute",
                  left: -6,
                  right: -6,
                  top: "52%",
                  height: 7,
                  background: COLORS.red,
                  transformOrigin: "left",
                  transform: `scaleX(${strike})`,
                }}
              />
            </span>{" "}
            to each other
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM: the "let them chat" trap */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the naive design" color={COLORS.amber} />
        <Panel accent="red" title="agents as a group chat" width={760}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 24, lineHeight: 2.0, color: COLORS.text }}>
            <div>researcher → composer&nbsp;&nbsp;<span style={{ color: COLORS.dim }}>"here's my draft…"</span></div>
            <div>composer → sender&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: COLORS.dim }}>"use this version?"</span></div>
            <div style={{ color: COLORS.red, marginTop: 6 }}>↳ lossy · unauditable · which version is real?</div>
          </div>
        </Panel>
        <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.dim, marginTop: 30 }}>
          messages drift. there's no single source of truth.
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="amber" />
  </AbsoluteFill>
);

/* 03 — BOARD: the shared blackboard */
type Row = { key: string; section: string; cur: boolean };
const ROWS: Row[] = [
  { key: "research_summary", section: "findings", cur: true },
  { key: "comparison_draft", section: "drafts", cur: true },
  { key: "comparison_draft", section: "drafts", cur: false },
  { key: "send_decision", section: "decisions", cur: true },
];
export const Scene03Board: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.board.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="mission_blackboard_entries · migration 113" color={COLORS.cyan} />
          <Panel accent="cyan" title="one shared, versioned board per mission" width={1080}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22 }}>
              <div style={{ display: "flex", color: COLORS.dim, paddingBottom: 8, borderBottom: `1px solid ${COLORS.line}` }}>
                <div style={{ width: 380 }}>entry_key</div>
                <div style={{ width: 280 }}>section</div>
                <div>is_current</div>
              </div>
              {ROWS.map((r, i) => {
                const appear = interpolate(frame, [20 + i * 16, 36 + i * 16], [0, 1], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                });
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 0",
                      opacity: appear,
                      color: r.cur ? COLORS.text : COLORS.dim,
                    }}
                  >
                    <div style={{ width: 380 }}>{r.key}</div>
                    <div style={{ width: 280, color: r.cur ? COLORS.cyan : COLORS.dim }}>{r.section}</div>
                    <div style={{ color: r.cur ? COLORS.green : COLORS.dim }}>
                      {r.cur ? "● true" : "○ false (superseded)"}
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
          <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 24 }}>
            findings · drafts · decisions · questions · scratch · answers
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={BOARD} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 04 — DEPS: the blocked_by DAG */
const DEP_PIPE: PipeNode[] = [
  { label: "step", value: "research", sub: "blocked_by: []", accent: "cyan" },
  { label: "step", value: "compose", sub: "blocked_by: research", accent: "green" },
  { label: "step", value: "send", sub: "blocked_by: compose", accent: "amber" },
];
export const Scene04Deps: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.deps.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="step dependencies · not a chat thread" color={COLORS.green} />
      <div style={{ width: 1560 }}>
        <PipelineGraph nodes={DEP_PIPE} startFrame={6} stepFrames={26} nodeWidth={300} />
      </div>
      <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.dim, marginTop: 46, textAlign: "center", maxWidth: 1280 }}>
        nextStep() won't dispatch a step until every id in its{" "}
        <span style={{ fontFamily: FONTS.mono, color: COLORS.text }}>blocked_by</span> is complete
      </div>
    </AbsoluteFill>
    <Caption lines={DEPS} accent="green" />
  </AbsoluteFill>
);

/* 05 — LOOP: the orchestrator dispatch loop */
const LOOP_PIPE: PipeNode[] = [
  { label: "1 · read", value: "BOARD", sub: "snapshot", accent: "cyan" },
  { label: "2 · pick", value: "READY STEP", sub: "deps met", accent: "green" },
  { label: "3 · run", value: "DISPATCH", sub: "agent step", accent: "lime" },
  { label: "4 · write", value: "OUTPUT", sub: "→ board", accent: "amber" },
];
export const Scene05Loop: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.loop.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="processMission · the dispatch loop" color={COLORS.lime} />
      <div style={{ width: 1680 }}>
        <PipelineGraph nodes={LOOP_PIPE} startFrame={6} stepFrames={22} nodeWidth={230} />
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim, marginTop: 46 }}>
        ↺ each tick: the board is the only thing the loop reads to decide what's next
      </div>
    </AbsoluteFill>
    <Caption lines={LOOP} accent="lime" />
  </AbsoluteFill>
);

/* 06 — CODE: producer writes → consumer reads */
const CODE_SNIPPET = `// PRODUCER step — the agent publishes to the shared board
blackboard_write({
  section: "drafts",          // 1 of 6 typed sections
  entry_key: "comparison_draft",
  value: { content: "...", confidence: 0.86 },
});
// append-only: a BEFORE INSERT trigger bumps version
// and flips the prior row's is_current → false

// CONSUMER step (blocked_by: ["compose"]) — no message sent.
// The orchestrator prepends the producer's output:
//
//   ## Outputs from prior steps in this mission
//   ### Step "compose"
//   <comparison_draft content>
//   ---
//   ## Your task
`;
export const Scene06Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={CODE_SNIPPET}
        language="javascript"
        filename="blackboard.ts · dispatch-step-agents.ts"
        highlightLines={[2, 5, 16]}
        revealFrames={260}
        fontSize={23}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="violet" />
  </AbsoluteFill>
);

/* 07 — GATE: expected_outputs verification */
export const Scene07Gate: React.FC = () => {
  const frame = useCurrentFrame();
  const reveal = interpolate(frame, [30, 60], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.gate.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="expected_outputs · trust, verified" color={COLORS.amber} />
          <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
            <Panel accent="red" title="agent claims success" width={500}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
                <div>"Done — draft written." ✓</div>
                <div style={{ color: COLORS.red, marginTop: 8 }}>…but skipped blackboard_write</div>
                <div style={{ color: COLORS.dim, marginTop: 8 }}>(LLMs do this under tool pressure)</div>
              </div>
            </Panel>
            <div style={{ opacity: reveal }}>
              <Panel accent="green" title="post-step verifier" width={500}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
                  <div>re-read board for the key</div>
                  <div style={{ color: COLORS.red }}>no is_current entry?</div>
                  <div style={{ color: COLORS.green, marginTop: 8 }}>→ step fails &amp; retries</div>
                </div>
              </Panel>
            </div>
          </div>
          <div style={{ marginTop: 30 }}>
            <Callout label="why it exists" accent="amber" width={840}>
              The empty-email bug: a composer "succeeded" without writing — the send step shipped a stale draft. The gate makes the write provable.
            </Callout>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={GATE} accent="amber" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={70}>
          Coordination isn't conversation.
          <br />
          <span style={{ color: COLORS.cyan }}>Shared memory</span> +{" "}
          <span style={{ color: COLORS.green }}>a dependency graph</span>.
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · multi-agent missions · build-in-public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
